import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';

const OutputControls = () => {
  const { latexOutput, showToast } = useAppContext();
  const [isExporting, setIsExporting] = useState(false);
  const [isPrintingPDF, setIsPrintingPDF] = useState(false);

  const exportToLatex = async () => {
    setIsExporting(true);
    
    try {
      // Use the existing copyAllLatex logic to get clean LaTeX
      const validLatexItems = latexOutput.filter(item => item.latex && !item.error);
      
      if (validLatexItems.length === 0) {
        showToast('No LaTeX to export', 'warning');
        return;
      }
      
      // Format expressions without numbering
      const formattedExpressions = validLatexItems.map((item) => {
        return `$$${item.latex}$$`;
      }).join('\n\n');
      
      // Create a complete LaTeX document
      // Update LaTeX document title (around line 26)
      const latexDocument = `\\documentclass[12pt]{article}
      \\usepackage{amsmath}
      \\usepackage{amsfonts}
      \\usepackage{amssymb}
      \\usepackage{mathtools}
      \\usepackage{esint}
      \\usepackage[margin=1in]{geometry}
      \\usepackage{fancyhdr}
      \\usepackage{datetime}
      \n\n\\title{./math Expressions}\n\\author{Generated by MathLatech}\n\\date{\\today}\n\n\\pagestyle{fancy}\n\\fancyhf{}\n\\rhead{MathLatech Export}\n\\lfoot{\\today}\n\\rfoot{Page \\thepage}\n\n\\begin{document}\n\\maketitle\n\n\\section{Mathematical Expressions}\n\nThis document contains ${validLatexItems.length} mathematical expression${validLatexItems.length === 1 ? '' : 's'} exported from MathLatech.\n\n${formattedExpressions}\n\n\\section{Raw ./math Code}\n\nFor reference, here is the raw ./math code for all expressions:\n\n\\begin{verbatim}\n${validLatexItems.map(item => item.latex).join('\n\n')}\n\\end{verbatim}\n\n\\end{document}`;
      
      // Update PDF HTML title (around line 70)
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>./math Expressions - MathLatech</title>
          <meta charset="utf-8">
          <script src="https://polyfill.io/v3/polyfill.min.js?features=es6"></script>
          <script id="MathJax-script" async src="https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js"></script>
          <script>
            window.MathJax = {
              tex: {
                inlineMath: [['$', '$'], ['\\\\(', '\\\\)']],
                displayMath: [['$$', '$$'], ['\\\\[', '\\\\]']]
              },
              startup: {
                ready: () => {
                  MathJax.startup.defaultReady();
                  setTimeout(() => {
                    window.print();
                  }, 2000);
                }
              }
            };
          </script>
          <style>
            @media print {
              body { margin: 0.5in; }
              .no-print { display: none; }
            }
            body {
              font-family: 'Times New Roman', serif;
              line-height: 1.8;
              max-width: 8.5in;
              margin: 0 auto;
              padding: 1in;
            }
            h1 {
              text-align: center;
              color: #333;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .expression {
              margin: 25px 0;
              padding: 20px;
              text-align: center;
            }
            .math-content {
              font-size: 1.3em;
              margin: 20px 0;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 0.9em;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 15px;
            }
            .no-print {
              background: #007bff;
              color: white;
              padding: 10px 20px;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              margin: 10px;
            }
          </style>
        </head>
        <body>
          <h1>./math Expressions</h1>
          <p style="text-align: center;"><strong>Generated by MathLatech</strong> | ${new Date().toLocaleDateString()}</p>
          
          <button class="no-print" onclick="window.print()">Print/Save as PDF</button>
          <button class="no-print" onclick="window.close()">Close</button>
          
          <div class="expressions">
            ${validLatexItems.map((item) => `
              <div class="expression">
                <div class="math-content">$$${item.latex}$$</div>
              </div>
            `).join('')}
          </div>
          
          <div class="footer">
            <p>This document contains ${validLatexItems.length} mathematical expression${validLatexItems.length === 1 ? '' : 's'}.</p>
            <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
        </body>
        </html>
      `;

      // Open in new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      showToast('PDF print dialog opened! Use Ctrl+P or Cmd+P to save as PDF.', 'success');
      
    } catch (error) {
      console.error('PDF export failed:', error);
      showToast('PDF export failed', 'error');
    } finally {
      setIsPrintingPDF(false);
    }
  };

  const copyAllLatex = () => {
    const allLatex = latexOutput
      .filter(item => item.latex && !item.error)
      .map(item => item.latex)
      .join('\n\n');
    
    if (allLatex) {
      navigator.clipboard.writeText(allLatex)
        .then(() => showToast('All LaTeX copied to clipboard', 'success'))
        .catch(() => showToast('Failed to copy to clipboard', 'error'));
    } else {
      showToast('No LaTeX to copy', 'warning');
    }
  };

  const exportToPDF = async () => {
    setIsPrintingPDF(true);
    
    try {
      const validLatexItems = latexOutput.filter(item => item.latex && !item.error);
      
      if (validLatexItems.length === 0) {
        showToast('No LaTeX to export', 'warning');
        return;
      }
      
      // Create HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>./math Expressions - MathLatech</title>
          <meta charset="utf-8">
          <script src="https://polyfill.io/v3/polyfill.min.js?features=es6"></script>
          <script id="MathJax-script" async src="https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js"></script>
          <script>
            window.MathJax = {
              tex: { inlineMath: [['$', '$'], ['\\(', '\\)']], displayMath: [['$$', '$$'], ['\\[', '\\]']] },
              chtml: { scale: 1.2 }
            };
          </script>
          <style>
            body { font-family: 'Times New Roman', serif; margin: 40px; line-height: 1.6; }
            h1 { text-align: center; color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px; }
            .expression { margin: 30px 0; padding: 20px; border: 1px solid #ddd; border-radius: 8px; background: #f9f9f9; }
            .math-content { font-size: 1.2em; text-align: center; margin: 15px 0; }
            .footer { margin-top: 40px; text-align: center; font-size: 0.9em; color: #666; border-top: 1px solid #ddd; padding-top: 15px; }
            .no-print { background: #007bff; color: white; padding: 10px 20px; border: none; border-radius: 5px; cursor: pointer; margin: 10px; }
          </style>
        </head>
        <body>
          <h1>./math Expressions</h1>
          <p style="text-align: center;"><strong>Generated by MathLatech</strong> | ${new Date().toLocaleDateString()}</p>
          
          <button class="no-print" onclick="window.print()">Print/Save as PDF</button>
          <button class="no-print" onclick="window.close()">Close</button>
          
          <div class="expressions">
            ${validLatexItems.map((item) => `
              <div class="expression">
                <div class="math-content">$$${item.latex}$$</div>
              </div>
            `).join('')}
          </div>
          
          <div class="footer">
            <p>This document contains ${validLatexItems.length} mathematical expression${validLatexItems.length === 1 ? '' : 's'}.</p>
            <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
        </body>
        </html>
      `;
  
      // Open in new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      showToast('PDF print dialog opened! Use Ctrl+P or Cmd+P to save as PDF.', 'success');
      
    } catch (error) {
      console.error('PDF export failed:', error);
      showToast('PDF export failed', 'error');
    } finally {
      setIsPrintingPDF(false);
    }
  };

  return (
    <div className="output-controls">
      <button 
        className="copy-all-button" 
        onClick={copyAllLatex}
        disabled={!latexOutput.some(item => item.latex && !item.error)}
      >
        Copy All LaTeX
      </button>
      
      <button 
        className="export-pdf-button" 
        onClick={exportToLatex}
        disabled={!latexOutput.some(item => item.latex && !item.error) || isExporting}
      >
        {isExporting ? 'Exporting...' : 'Export LaTeX'}
      </button>
      
      <button 
        className="export-pdf-button" 
        onClick={exportToPDF}
        disabled={!latexOutput.some(item => item.latex && !item.error) || isPrintingPDF}
        style={{ marginLeft: '10px', backgroundColor: '#28a745' }}
      >
        {isPrintingPDF ? 'Opening...' : 'Export PDF'}
      </button>
    </div>
  );
};

export default OutputControls;